/**
 * Client-side permission helpers (keep in sync with backend Admin.hasPermission).
 */

/** Normalize admin.permissions to a string[] (API should send an array; defend against bad shapes). */
function permissionList(user) {
    const raw = user?.permissions;
    if (Array.isArray(raw)) {
        return raw.filter((p) => typeof p === 'string');
    }
    if (raw && typeof raw === 'object') {
        return Object.values(raw).filter((p) => typeof p === 'string');
    }
    return [];
}

export function adminHasMenuView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('view_menu') || p.includes('manage_menu') || p.includes('manage_content');
}

export function adminHasMenuManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('manage_menu') || p.includes('manage_content');
}

export function adminHasContactsView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('view_contacts') || p.includes('manage_contacts');
}

export function adminHasContactsManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('manage_contacts');
}

export function adminHasReservationsView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('view_reservations') || p.includes('manage_reservations');
}

export function adminHasReservationsManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('manage_reservations');
}

export function adminHasSubscribersView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('view_subscribers') || p.includes('manage_subscribers');
}

export function adminHasSubscribersManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('manage_subscribers');
}

export function adminHasProductsView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('view_products') || p.includes('manage_products') || p.includes('manage_content');
}

export function adminHasProductsManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('manage_products') || p.includes('manage_content');
}

export function adminHasPropertiesView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('view_properties') || p.includes('manage_properties') || p.includes('manage_content');
}

export function adminHasPropertiesManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('manage_properties') || p.includes('manage_content');
}

export function adminHasCarsView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('view_cars') || p.includes('manage_cars') || p.includes('manage_content');
}

export function adminHasCarsManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('manage_cars') || p.includes('manage_content');
}

export function adminHasFoodOrdersView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('view_food_orders') || p.includes('manage_food_orders');
}

export function adminHasFoodOrdersManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('manage_food_orders');
}

export function adminHasProductOrdersView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('view_product_orders') || p.includes('manage_product_orders');
}

export function adminHasProductOrdersManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('manage_product_orders');
}

export function adminHasAppUsersView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('view_app_users') || p.includes('manage_app_users');
}

export function adminHasAppUsersManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('manage_app_users');
}

export function adminHasCouponsView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('view_coupons') || p.includes('manage_coupons');
}

export function adminHasCouponsManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('manage_coupons');
}

/** Analytics / site visits charts (matches backend analytics routes when gated). */
export function adminHasAnalyticsView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('view_analytics');
}

/** Store settings: general business settings & opening hours (not personal account settings). */
export function adminHasSettingsView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('view_settings') || p.includes('manage_settings');
}

export function adminHasSettingsManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('manage_settings');
}

/** Admin directory: list and open profiles (not create/edit/suspend/delete). */
export function adminHasAdminsView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    if (['owner', 'admin'].includes(user.orgRole)) return true;
    const p = permissionList(user);
    return p.includes('view_admins') || p.includes('manage_admins');
}

export function adminHasAdminsManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    if (['owner', 'admin'].includes(user.orgRole)) return true;
    const p = permissionList(user);
    return p.includes('manage_admins');
}

export function adminHasRolesManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('manage_roles') || p.includes('manage_admins');
}

/** Login security dashboard: failed logins, locks, suspicious IPs (read-only without manage). */
export function adminHasSecurityView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('view_security') || p.includes('manage_security');
}

export function adminHasSecurityManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('manage_security');
}

export function adminHasHeroView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('view_hero') || p.includes('manage_hero') || p.includes('manage_content');
}

export function adminHasHeroManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('manage_hero') || p.includes('manage_content');
}

export function adminHasBannerView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('view_banner') || p.includes('manage_banner') || p.includes('manage_content');
}

export function adminHasBannerManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('manage_banner') || p.includes('manage_content');
}

export function adminHasPhotoGalleryView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('view_photo_gallery') || p.includes('manage_photo_gallery') || p.includes('manage_content');
}

export function adminHasPhotoGalleryManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('manage_photo_gallery') || p.includes('manage_content');
}

export function adminHasTestimonialsView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('view_testimonials') || p.includes('manage_testimonials') || p.includes('manage_content');
}

export function adminHasTestimonialsManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('manage_testimonials') || p.includes('manage_content');
}

export function adminHasFaqView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('view_faq') || p.includes('manage_faq') || p.includes('manage_content');
}

export function adminHasFaqManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('manage_faq') || p.includes('manage_content');
}

export function adminHasEventsView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('view_events') || p.includes('manage_events') || p.includes('manage_content');
}

export function adminHasEventsManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('manage_events') || p.includes('manage_content');
}

export function adminHasNotificationsView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return (
        p.includes('view_notifications') ||
        p.includes('manage_notifications') ||
        p.includes('manage_content')
    );
}

export function adminHasNotificationsManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return p.includes('manage_notifications') || p.includes('manage_content');
}

/** Any Content Blocks hub section (hero, banner, gallery, testimonials, or FAQ). */
export function adminHasContentBlocksHubAccess(user) {
    return (
        adminHasHeroView(user) ||
        adminHasBannerView(user) ||
        adminHasPhotoGalleryView(user) ||
        adminHasTestimonialsView(user) ||
        adminHasFaqView(user) ||
        adminHasEventsView(user)
    );
}

function teamEmployeesViewPerms(p) {
    return (
        p.includes('view_employees') ||
        p.includes('manage_employees') ||
        p.includes('view_team_employees') ||
        p.includes('manage_team_employees') ||
        p.includes('view_team') ||
        p.includes('manage_team') ||
        p.includes('manage_content')
    );
}

function teamEmployeesManagePerms(p) {
    return (
        p.includes('manage_employees') ||
        p.includes('manage_team_employees') ||
        p.includes('manage_team') ||
        p.includes('manage_content')
    );
}

function teamAttendanceViewPerms(p) {
    return (
        p.includes('view_attendance') ||
        p.includes('manage_attendance') ||
        p.includes('view_team_attendance') ||
        p.includes('manage_team_attendance') ||
        p.includes('view_team') ||
        p.includes('manage_team') ||
        p.includes('manage_content')
    );
}

function teamAttendanceManagePerms(p) {
    return (
        p.includes('manage_attendance') ||
        p.includes('manage_team_attendance') ||
        p.includes('manage_team') ||
        p.includes('manage_content')
    );
}

function teamLeaveRequestsViewPerms(p) {
    return (
        p.includes('view_leave_requests') ||
        p.includes('manage_leave_requests') ||
        p.includes('view_team_leave_requests') ||
        p.includes('manage_team_leave_requests') ||
        p.includes('view_team') ||
        p.includes('manage_team') ||
        p.includes('manage_content')
    );
}

function teamLeaveRequestsManagePerms(p) {
    return (
        p.includes('manage_leave_requests') ||
        p.includes('manage_team_leave_requests') ||
        p.includes('manage_team') ||
        p.includes('manage_content')
    );
}

/** Any team module section (overview sidebar, home card). */
export function adminHasTeamView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return (
        teamEmployeesViewPerms(p) ||
        teamAttendanceViewPerms(p) ||
        teamLeaveRequestsViewPerms(p)
    );
}

/** Legacy umbrella — true if admin can manage any team area. */
export function adminHasTeamManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const p = permissionList(user);
    return (
        teamEmployeesManagePerms(p) ||
        teamAttendanceManagePerms(p) ||
        teamLeaveRequestsManagePerms(p)
    );
}

export function adminHasTeamEmployeesView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    return teamEmployeesViewPerms(permissionList(user));
}

export function adminHasTeamEmployeesManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    return teamEmployeesManagePerms(permissionList(user));
}

export function adminHasTeamAttendanceView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    return teamAttendanceViewPerms(permissionList(user));
}

export function adminHasTeamAttendanceManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    return teamAttendanceManagePerms(permissionList(user));
}

export function adminHasTeamLeaveRequestsView(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    return teamLeaveRequestsViewPerms(permissionList(user));
}

export function adminHasTeamLeaveRequestsManage(user) {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    return teamLeaveRequestsManagePerms(permissionList(user));
}
